import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '@/app/auth/context';

export type MemberRelatedPage =
  | 'detail'
  | 'edit'
  | 'integrations'
  | 'events'
  | 'portfolio'
  | 'president-survey'
  | 'event-result';

/**
 * 同一会員の関連ページへの導線（D-278）。
 * 詳細／編集／連携／イベント／ポートフォリオを行き来するとき、一覧に戻って探し直さなくてよいようにする。
 */
export function MemberRelatedNav({
  memberId,
  current,
}: {
  memberId: string;
  current: MemberRelatedPage;
}): ReactElement {
  const { user } = useAuth();
  const isOffice = user?.active_role === 'office';
  const isStaffViewer =
    user?.active_role === 'director' || user?.active_role === 'success';

  const links: { key: MemberRelatedPage; label: string; to: string; show: boolean }[] = [
    { key: 'detail', label: '会員詳細', to: `/members/${memberId}`, show: true },
    {
      key: 'edit',
      label: 'プロフィール編集',
      to: `/members/${memberId}/edit`,
      show: isOffice,
    },
    {
      key: 'integrations',
      label: '連携設定編集',
      to: `/members/${memberId}/integrations`,
      show: isOffice,
    },
    {
      key: 'events',
      label: '参加イベント一覧',
      to: `/members/${memberId}/events`,
      show: isOffice,
    },
    {
      // 社長への聞き取り結果は事務局が代理入力する（D-146・D-315）
      key: 'president-survey',
      label: '社長アンケート',
      to: `/members/${memberId}/president-survey`,
      show: isOffice,
    },
    {
      key: 'portfolio',
      label: '年間ポートフォリオ',
      to: `/members/${memberId}/portfolio`,
      // 理事・サクセスは会員詳細からは入れない。一覧またはポートフォリオ画面内のみ（D-330）
      show: isOffice || (isStaffViewer && current === 'portfolio'),
    },
  ];

  const visible = links.filter((link) => link.show);
  if (visible.length <= 1) return <></>;

  return (
    <nav
      className="mb-4 flex flex-wrap items-center gap-2"
      aria-label="この会員の関連ページ"
    >
      <span className="text-wptext-muted">この会員:</span>
      {visible.map((link) => {
        const isCurrent =
          link.key === current ||
          (current === 'event-result' && link.key === 'events');
        if (isCurrent) {
          return (
            <span
              key={link.key}
              className="rounded-sm border border-wpaccent bg-wpaccent/10 px-2 py-1 text-wptext-strong"
              aria-current="page"
            >
              {link.label}
            </span>
          );
        }
        return (
          <Link key={link.key} to={link.to} className="btn-secondary px-2 py-1">
            {link.label}
          </Link>
        );
      })}
    </nav>
  );
}
